import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Reallybot from '../../Logo/Reallybot.png';
import Notifications from '../notificationfunction/fetchNotification.js';
import SurveyNavPopup from '../survey/SurveyNavPopup';
import IncomingEventsNavPopup from '../eventAttendance/IncomingEventsNavPopup';
import JobsNavPopup from '../jobs/JobsNavPopup';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import { getApiBaseUrl } from '../../config/api';
import { isAdmin, isEmployer } from '../../utils/userRole';

const API_URL = getApiBaseUrl();

const getFullPicUrl = (userOrPic) =>
  typeof userOrPic === 'object' && userOrPic !== null
    ? resolveAvatarUrl(userOrPic, API_URL)
    : resolveAvatarUrl({ profile_pic: userOrPic }, API_URL);

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '50px',
  width: '100%',
  padding: '8px 20px',
  boxSizing: 'border-box',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(12px) saturate(180%)',
  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
  backgroundImage:
    'linear-gradient(to right, rgba(255, 255, 255, 0.15), rgba(173, 216, 230, 0.25))',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
};

const menuItemStyle = {
  padding: '10px 20px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
};

/**
 * Shared glass navbar for all login-required pages.
 */
export default function AuthenticatedNavbar({ user, onLogout }) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const wrapRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showDropdown) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showDropdown]);

  const goHome = () => {
    navigate(isAdmin(user) ? '/admin' : '/home');
  };

  const closeAnd = (fn) => () => {
    setShowDropdown(false);
    fn();
  };

  const displayName =
    user?.first_name || user?.firstname || user?.username || 'User';

  return (
    <header style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={Reallybot}
          alt="Logo"
          onClick={goHome}
          style={{ height: 30, marginRight: 12, cursor: 'pointer' }}
        />
      </div>

      <div
        ref={wrapRef}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <SurveyNavPopup user={user} page="main" adminMode={isAdmin(user)} />
        <IncomingEventsNavPopup user={user} />
        <JobsNavPopup user={user} />
        <Notifications userId={user?.id} />

        <div
          onClick={() => setShowDropdown((prev) => !prev)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            color: '#004d70',
            fontWeight: 'bold',
            userSelect: 'none',
          }}
        >
          <p
            style={{
              margin: '10px',
              display: isMobile ? 'none' : 'block',
              fontSize: '14px',
            }}
          >
            Welcome back, {displayName}
          </p>
          <img
            src={getFullPicUrl(user)}
            onError={handleAvatarError}
            alt="avatar"
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              objectFit: 'cover',
              marginRight: 8,
            }}
          />
          <i
            className="fa fa-caret-down"
            style={{ marginLeft: 8, marginRight: 10 }}
          />
        </div>

        {showDropdown && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              marginTop: 10,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              width: 240,
              overflow: 'hidden',
              fontFamily: 'sans-serif',
            }}
          >
            <div onClick={closeAnd(goHome)} style={menuItemStyle}>
              <i className="fa fa-home" style={{ marginRight: 6 }} />
              Home
            </div>
            <div
              onClick={closeAnd(() => navigate('/myprofileinfo'))}
              style={menuItemStyle}
            >
              <i className="fa fa-user" style={{ marginRight: 6 }} />
              My Profile
            </div>
            <div
              onClick={closeAnd(() => navigate('/network'))}
              style={menuItemStyle}
            >
              <i className="fa fa-users" style={{ marginRight: 6 }} />
              My Network
            </div>
            <div
              onClick={closeAnd(() => navigate('/groups/groupinpage'))}
              style={menuItemStyle}
            >
              <i className="fa fa-layer-group" style={{ marginRight: 6 }} />
              Groups
            </div>
            <div
              onClick={closeAnd(() => navigate('/blogs'))}
              style={menuItemStyle}
            >
              <i className="fa fa-book" style={{ marginRight: 6 }} />
              Blogs
            </div>
            <div
              onClick={closeAnd(() => navigate('/learning'))}
              style={menuItemStyle}
            >
              <i className="fa fa-comments" style={{ marginRight: 6 }} />
              Learning Q&A
            </div>
            <div
              onClick={closeAnd(() => navigate('/my-learning'))}
              style={menuItemStyle}
            >
              <i className="fa fa-graduation-cap" style={{ marginRight: 6 }} />
              My Learning
            </div>
            <div
              onClick={closeAnd(() => navigate('/saved'))}
              style={menuItemStyle}
            >
              <i className="fa fa-bookmark" style={{ marginRight: 6 }} />
              Saved
            </div>
            <div
              onClick={closeAnd(() => navigate('/my-applications'))}
              style={menuItemStyle}
            >
              <i className="fa fa-briefcase" style={{ marginRight: 6 }} />
              My Applications
            </div>
            {isEmployer(user) && (
              <div
                onClick={closeAnd(() => navigate('/hiring/applications'))}
                style={menuItemStyle}
              >
                <i className="fa fa-inbox" style={{ marginRight: 6 }} />
                Job Applications
              </div>
            )}
            {isEmployer(user) && (
              <div
                onClick={closeAnd(() => navigate('/event-attendance'))}
                style={menuItemStyle}
              >
                <i className="fa fa-calendar-check" style={{ marginRight: 6 }} />
                Attend Events
              </div>
            )}
            <div
              onClick={closeAnd(() => navigate('/profile'))}
              style={menuItemStyle}
            >
              <i className="fa fa-file-alt" style={{ marginRight: 6 }} />
              My Post
            </div>
            <div
              onClick={closeAnd(() => navigate('/aboutus'))}
              style={menuItemStyle}
            >
              <i className="fa fa-question-circle" style={{ marginRight: 6 }} />
              About us
            </div>
            {isAdmin(user) && (
              <>
                <div
                  onClick={closeAnd(() => navigate('/usermanagement'))}
                  style={menuItemStyle}
                >
                  <i className="fa fa-users-cog" style={{ marginRight: 6 }} />
                  User Management
                </div>
                <div
                  onClick={closeAnd(() => navigate('/hiringmanagement'))}
                  style={menuItemStyle}
                >
                  <i className="fa fa-briefcase" style={{ marginRight: 6 }} />
                  Hiring Management
                </div>
                <div
                  onClick={closeAnd(() => navigate('/analytics'))}
                  style={menuItemStyle}
                >
                  <i className="fa fa-chart-line" style={{ marginRight: 6 }} />
                  Dashboard
                </div>
                <div
                  onClick={closeAnd(() => navigate('/assistant'))}
                  style={menuItemStyle}
                >
                  <i className="fa fa-robot" style={{ marginRight: 6 }} />
                  COOL Assistant
                </div>
              </>
            )}
            <div
              onClick={closeAnd(() => onLogout && onLogout())}
              style={{ ...menuItemStyle, borderBottom: 'none' }}
            >
              <i className="fa fa-sign-out-alt" style={{ marginRight: 6 }} />
              Logout
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
